import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { useForm } from 'react-hook-form';

import { types } from '../../../constants/G5MessagesTypes';
import './style.scss';

const fontSizeStyle = '1.5rem';

const useStyles = makeStyles({
  root: {
    margin: '5px 10px',
  },
  margin: {
    margin: '3px 5px',
    display: 'block',
    fontSize: fontSizeStyle,
    padding: '5px',
  },
  largeFont: {
    fontSize: fontSizeStyle,
    width: '-webkit-fill-available',
  },
  fields: {
    padding: '5px 0',
    display: 'grid',
    gridTemplateColumns: '140px 100px',
    gridRowGap: '3px',
  },
  // Stacked variant: label sits on its own row above a full-width control.
  // Used by forms with long labels / wide select values that don't fit the
  // narrow two-column grid (e.g. Set PSM, Set Second Keepalive, Set Fast Burst).
  fieldsStacked: {
    padding: '5px 0',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: '2px',
  },
  stackedLabel: {
    marginTop: '4px',
  },
  stackedControl: {
    width: '-webkit-fill-available',
    boxSizing: 'border-box',
  },
  errorField: {
    backgroundColor: '#ffbbbb',
  },
  buttons: {
    display: 'grid',
    gridTemplateColumns: '100px 140px',
    gridRowGap: '3px',
    columnGap: '5px',
  },
  labelStyle: {
    minInlineSize: 'max-content',
  },
});

export default function OperationForm(props, context) {
  const { fields, type } = props;

  const classes = useStyles();
  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    setError,
    errors,
    clearErrors,
    watch,
  } = useForm({
    shouldUnregister: false  // Keep form values when inputs unmount
  });

  React.useEffect(() => {
    fields.forEach((field) => {
      const val = field.attr && field.attr.defaultValue;
      if (field.type === 'checkbox') {
        // For checkboxes, explicitly set the value to ensure proper state update
        setValue(field.id, val || false);
      } else {
        setValue(field.id, val);
      }
    });
  }, [fields, setValue]);

  const checkValues = (values) => {
    const maxTotal = 1430;
    return (
      // values.corrInterval * values.corrNumSamples +
      values.noiseInterval * values.noiseNumSamples > maxTotal
    );
  };

  const submitFunc = handleSubmit((data) => {
    const values = getValues();
    let haveErrors = false;
    if (props.type === types.Scheduler) {
      if (checkValues(values)) {
        haveErrors = true;
        props.notify('sum interval is to big', 'error');
      }
    }
    if (!haveErrors) {
      props.onSubmit(data);
    }
  });
  const { stacked } = props;
  const labelClass = stacked ? classes.stackedLabel : classes.labelStyle;
  const controlClass = stacked ? classes.stackedControl : undefined;

  return (
    <form className={classes.root} onSubmit={submitFunc}>
      <Box
        component='fieldset'
        className={stacked ? classes.fieldsStacked : classes.fields}
      >
        {fields.map((field) => {
          switch (field.type) {
            case 'select':
              return (
                <React.Fragment key={field.id}>
                  <label className={stacked ? labelClass : undefined} htmlFor={field.id}>
                    {field.title}
                  </label>
                  <select
                    ref={register}
                    id={field.id}
                    name={field.id}
                    className={controlClass}
                    disabled={field.disabled || false}
                    {...field.attr}
                  >
                    {field.options?.map((option) => (
                      <option
                        key={option.optionKey}
                        value={option?.value || option.optionKey}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </React.Fragment>
              );
            case 'checkbox':
              return (
                <React.Fragment key={field.id}>
                  <label className={labelClass} htmlFor={field.id}>
                    {field.title}
                  </label>
                  <input
                    ref={register}
                    id={field.id}
                    name={field.id}
                    type="checkbox"
                    defaultChecked={field.attr?.defaultValue || false}
                    disabled={field.disabled || false}
                  />
                </React.Fragment>
              );
            default:
              return (
                <React.Fragment key={field.id}>
                  <label className={labelClass} htmlFor={field.id}>
                    {field.title}
                  </label>
                  <input
                    ref={register}
                    id={field.id}
                    name={field.id}
                    type={field.type || 'text'}
                    className={
                      [controlClass, errors[field.id] && classes.errorField]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                    placeholder={errors[field.id] && errors[field.id].message}
                    onChange={() => errors[field.id] && clearErrors(field.id)}
                    disabled={field.disabled || false}
                    {...(field.attr || null)}
                  />
                </React.Fragment>
              );
          }
        })}
      </Box>
      <Box className={classes.buttons}>
        <Button
          className={classes.largeFont}
          variant='contained'
          color='primary'
          type='submit'
          disabled={!props.enable}
        >
          {context.t('submit')}
        </Button>
        <Button
          className={classes.largeFont}
          variant='contained'
          color='inherit'
          disabled={!props.advancedOptions}
          onClick={() => {
            clearErrors();
            const values = getValues();
            let haveErrors = false;
            if (props.type === types.Scheduler) {
              if (checkValues(values)) {
                haveErrors = true;
                props.notify('sum interval is to big', 'error');
              }
            }

            Object.entries(values).forEach(([paramKey, value]) => {
              if (value == null) {
                if (!haveErrors) haveErrors = true;

                setError(paramKey, {
                  type: 'required',
                  message: context.t('required'),
                });
              }
            });

            if (!haveErrors) {
              props.onSaveDefault(values);
            }
          }}
        >
          {context.t('set_default')}
        </Button>
      </Box>
    </form>
  );
}

OperationForm.contextTypes = {
  t: PropTypes.func.isRequired,
};

OperationForm.propTypes = {
  advancedOptions: PropTypes.bool,
  enable: PropTypes.bool,
  stacked: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSaveDefault: PropTypes.func,
  notify: PropTypes.func.isRequired, // Add this line
};
